import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../ui/dialog";
import { ConfirmDialog } from "../shared/ConfirmDialog";
import { toast } from "sonner";
import {
  Radar, RefreshCw, ExternalLink, Save, EyeOff, Loader2,
  AlertTriangle, Clock, CheckCircle2, Building2, Plus, Sparkles,
  Trash2, Target, X,
} from "lucide-react";

const SOURCE_COLORS = {
  cdti: { bg: "bg-emerald-100", text: "text-emerald-800", label: "CDTI" },
  eu:   { bg: "bg-blue-100",    text: "text-blue-800",    label: "EU" },
  manual: { bg: "bg-slate-200", text: "text-slate-700",   label: "Manual" },
};

function DaysBadge({ days }) {
  if (days === null || days === undefined) {
    return <Badge variant="outline" className="text-slate-500">Fecha TBD</Badge>;
  }
  if (days < 0) return <Badge variant="outline" className="text-slate-400 line-through">Cerrada</Badge>;
  if (days <= 7) return <Badge className="bg-red-500 hover:bg-red-500 text-white gap-1"><AlertTriangle className="w-3 h-3" />Urgente · {days}d</Badge>;
  if (days <= 30) return <Badge className="bg-amber-500 hover:bg-amber-500 text-white gap-1"><Clock className="w-3 h-3" />{days} días</Badge>;
  return <Badge variant="outline" className="text-emerald-700 border-emerald-300 gap-1"><CheckCircle2 className="w-3 h-3" />{days} días</Badge>;
}

function EligibilityBadge({ eligibility }) {
  const applies = eligibility?.applies_to_large_company;
  if (applies === undefined || applies === null) return null;
  if (applies === false) {
    return (
      <Badge className="bg-slate-200 text-slate-600 hover:bg-slate-200 gap-1" title={eligibility.company_size_note || "Solo PYME"} data-testid="eligibility-badge-no">
        <Building2 className="w-3 h-3" /> No aplica (gran empresa)
      </Badge>
    );
  }
  return (
    <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 gap-1" title={eligibility.company_size_note || "Aplica a gran empresa"} data-testid="eligibility-badge-yes">
      <Building2 className="w-3 h-3" /> Aplica gran empresa
    </Badge>
  );
}

const daysUntil = (deadline) => {
  if (!deadline) return null;
  const diff = Math.ceil((new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24));
  return diff;
};

function toUnified(c, origin) {
  if (origin === "manual") {
    return {
      id: c.id, origin: "manual", source: "manual",
      title: c.name, program: c.program, description: c.description, url: c.url,
      deadline: c.deadline, deadline_text: null,
      days_until_deadline: daysUntil(c.deadline),
      budget: (c.funding_min || c.funding_max) ? `€${(c.funding_min || 0).toLocaleString()} - €${(c.funding_max || 0).toLocaleString()}` : null,
      eligibility: null, keywords_matched: [], assignments: c.assignments || [],
      saved_as_grant_id: null, agency: c.agency,
    };
  }
  return { ...c, origin: "radar", assignments: c.assignments || [] };
}

export default function GrantsRadarTab() {
  const { token, isAdmin } = useAuth();
  const [radarCalls, setRadarCalls] = useState([]);
  const [manualCalls, setManualCalls] = useState([]);
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sourceFilter, setSourceFilter] = useState("all");
  const [urgencyFilter, setUrgencyFilter] = useState("open"); // open | urgent | all
  const [hideIneligible, setHideIneligible] = useState(true);
  const [saving, setSaving] = useState({});
  const [assignItem, setAssignItem] = useState(null); // unified item
  const [assignProjectId, setAssignProjectId] = useState("");
  const [assignYear, setAssignYear] = useState(String(new Date().getFullYear()));
  const [assignAmount, setAssignAmount] = useState("");
  const [assignSaving, setAssignSaving] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [aiSearching, setAiSearching] = useState(false);
  const [aiResults, setAiResults] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [formData, setFormData] = useState({
    name: "", program: "", agency: "", description: "",
    funding_min: "", funding_max: "", deadline: "", url: "",
  });

  const fetchAll = async () => {
    try {
      const [list, s, manual, projs] = await Promise.all([
        api.getGrantsRadar(token, { source: sourceFilter === "all" || sourceFilter === "manual" ? undefined : sourceFilter }),
        api.getGrantsRadarStats(token),
        api.getGrantCalls(token),
        api.getPg07Projects(token),
      ]);
      setRadarCalls(list);
      setStats(s);
      setManualCalls(manual);
      setProjects(projs);
    } catch (e) {
      toast.error("Error cargando radar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceFilter, token]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await api.refreshGrantsRadar(token);
      toast.success(`Radar actualizado: ${res.refresh.new} nuevas, ${res.refresh.updated} actualizadas`);
      await fetchAll();
    } catch {
      toast.error("Error al actualizar (puede tardar hasta 90s)");
    } finally {
      setRefreshing(false);
    }
  };

  const handleDismiss = async (id) => {
    try {
      await api.dismissGrantsRadar(token, id);
      setRadarCalls((prev) => prev.filter((c) => c.id !== id));
      toast.success("Convocatoria ocultada");
    } catch { toast.error("Error"); }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteManual = (id, name) => setDeleteTarget({ id, name });

  const confirmDeleteManual = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.deleteGrantCall(token, deleteTarget.id);
      setManualCalls((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      toast.success("Convocatoria eliminada");
    } catch { toast.error("Error al eliminar"); }
    finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleSaveAsGrant = async (id) => {
    setSaving({ ...saving, [id]: true });
    try {
      await api.saveRadarAsGrant(token, id);
      toast.success("Guardada en Subvenciones");
      await fetchAll();
    } catch { toast.error("Error"); }
    finally { setSaving({ ...saving, [id]: false }); }
  };

  const handleAISearch = async () => {
    if (!searchQuery.trim()) return;
    setAiSearching(true);
    try {
      const results = await api.aiSearch(token, searchQuery, "grants");
      setAiResults(results);
    } catch {
      toast.error("Error en la búsqueda IA");
    } finally {
      setAiSearching(false);
    }
  };

  const handleCreateGrant = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const payload = {
        ...formData,
        funding_min: formData.funding_min ? parseFloat(formData.funding_min) : null,
        funding_max: formData.funding_max ? parseFloat(formData.funding_max) : null,
        deadline: formData.deadline ? new Date(formData.deadline).toISOString() : null,
      };
      await api.createGrantCall(token, payload);
      toast.success("Convocatoria creada");
      setCreateOpen(false);
      setFormData({ name: "", program: "", agency: "", description: "", funding_min: "", funding_max: "", deadline: "", url: "" });
      fetchAll();
    } catch {
      toast.error("Error al crear convocatoria");
    } finally {
      setFormLoading(false);
    }
  };

  const openAssign = (item) => {
    setAssignItem(item);
    setAssignProjectId("");
    setAssignYear(String(new Date().getFullYear()));
    setAssignAmount("");
  };

  const submitAssign = async () => {
    if (!assignItem || !assignProjectId || !assignYear) { toast.error("Selecciona proyecto y año"); return; }
    const proj = projects.find((p) => p.id === assignProjectId);
    setAssignSaving(true);
    try {
      const payload = { project_id: assignProjectId, project_name: proj?.name || "", year: assignYear, expected_amount: assignAmount ? parseFloat(assignAmount) : undefined };
      if (assignItem.origin === "manual") {
        await api.addGrantCallAssignment(token, assignItem.id, payload);
      } else {
        await api.addRadarAssignment(token, assignItem.id, payload);
      }
      toast.success(`Marcada como Solicitada → ${proj?.name} (${assignYear})`);
      setAssignItem(null);
      fetchAll();
    } catch {
      toast.error("Error al asignar");
    } finally {
      setAssignSaving(false);
    }
  };

  const removeAssignment = async (item, idx) => {
    try {
      if (item.origin === "manual") await api.removeGrantCallAssignment(token, item.id, idx);
      else await api.removeRadarAssignment(token, item.id, idx);
      toast.success("Asignación eliminada");
      fetchAll();
    } catch { toast.error("Error"); }
  };

  const merged = [
    ...radarCalls.map((c) => toUnified(c, "radar")),
    ...manualCalls.map((c) => toUnified(c, "manual")),
  ];

  const filtered = merged.filter((c) => {
    if (sourceFilter !== "all" && c.source !== sourceFilter) return false;
    if (hideIneligible && c.eligibility?.applies_to_large_company === false) return false;
    if (urgencyFilter === "open") return c.days_until_deadline === null || c.days_until_deadline >= 0;
    if (urgencyFilter === "urgent") return c.days_until_deadline !== null && c.days_until_deadline >= 0 && c.days_until_deadline <= 30;
    return true;
  });

  return (
    <div className="space-y-6" data-testid="grants-radar-tab">
      {/* Hero */}
      <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 overflow-hidden">
        <CardContent className="p-6 flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-700 font-semibold mb-2">
              <Radar className="w-4 h-4" /> Radar Convocatorias
            </div>
            <h2 className="text-2xl font-bold text-slate-900">Fuentes automáticas · CDTI + Horizon + LIFE + manuales</h2>
            <p className="text-sm text-slate-600 mt-1">
              Se actualiza cada día a las 07:00. Marca una convocatoria como "Solicitada" asignándola a uno o varios proyectos y años para tenerlo todo trazado.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="ai-search-grants-btn">
                  <Sparkles className="w-4 h-4 mr-2" />Buscar con IA
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Búsqueda de Convocatorias con IA</DialogTitle>
                  <DialogDescription>Busca convocatorias CDTI, Horizon Europe y otros programas</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Ej: economía circular, residuos, energías renovables..."
                      onKeyDown={(e) => e.key === "Enter" && handleAISearch()} />
                    <Button onClick={handleAISearch} disabled={aiSearching}>
                      {aiSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Buscar"}
                    </Button>
                  </div>
                  {aiResults && (
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <h4 className="font-medium mb-2">Resultados</h4>
                      <pre className="whitespace-pre-wrap text-sm">{aiResults.results}</pre>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="create-grant-btn">
                  <Plus className="w-4 h-4 mr-2" />Añadir manual
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Registrar Convocatoria manual</DialogTitle>
                  <DialogDescription>Se mostrará junto a las del radar automático, marcada como "Manual"</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateGrant} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nombre *</Label>
                    <Input id="name" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Horizon Europe - Green Deal" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="program">Programa</Label>
                      <Input id="program" value={formData.program} onChange={(e) => setFormData({ ...formData, program: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="agency">Agencia</Label>
                      <Input id="agency" value={formData.agency} onChange={(e) => setFormData({ ...formData, agency: e.target.value })} placeholder="CDTI" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">Descripción</Label>
                    <Textarea id="description" rows={2} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="funding_min">Financiación mín. (€)</Label>
                      <Input id="funding_min" type="number" value={formData.funding_min} onChange={(e) => setFormData({ ...formData, funding_min: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="funding_max">Financiación máx. (€)</Label>
                      <Input id="funding_max" type="number" value={formData.funding_max} onChange={(e) => setFormData({ ...formData, funding_max: e.target.value })} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="deadline">Fecha límite</Label>
                    <Input id="deadline" type="date" value={formData.deadline} onChange={(e) => setFormData({ ...formData, deadline: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="url">URL</Label>
                    <Input id="url" type="url" value={formData.url} onChange={(e) => setFormData({ ...formData, url: e.target.value })} placeholder="https://..." />
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
                    <Button type="submit" disabled={formLoading}>
                      {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Crear
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            {isAdmin && (
              <Button onClick={handleRefresh} disabled={refreshing} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="radar-refresh-btn">
                {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Actualizar ahora
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <StatCard label="Radar automático" value={stats.total} color="text-slate-900" />
          <StatCard label="Manuales" value={manualCalls.length} color="text-slate-600" />
          <StatCard label="Abiertas" value={stats.open_count} color="text-emerald-600" />
          <StatCard label="< 30 días" value={stats.urgent_count} color="text-amber-600" />
          <StatCard label="< 7 días" value={stats.critical_count} color="text-red-600" />
          <StatCard label="Nuevas 7d" value={stats.new_last_7d} color="text-blue-600" />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-auto min-w-[150px]" data-testid="radar-source-filter">
            <SelectValue placeholder="Fuente" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las fuentes</SelectItem>
            <SelectItem value="cdti">CDTI</SelectItem>
            <SelectItem value="eu">EU (Horizon/LIFE)</SelectItem>
            <SelectItem value="manual">Manual</SelectItem>
          </SelectContent>
        </Select>
        <Select value={urgencyFilter} onValueChange={setUrgencyFilter}>
          <SelectTrigger className="w-auto min-w-[170px]" data-testid="radar-urgency-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Solo abiertas</SelectItem>
            <SelectItem value="urgent">Urgentes (&lt; 30d)</SelectItem>
            <SelectItem value="all">Todas (incl. cerradas)</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-sm text-slate-500 ml-auto">{filtered.length} convocatorias</div>
        <Button
          size="sm"
          variant={hideIneligible ? "default" : "outline"}
          className={hideIneligible ? "bg-slate-700 hover:bg-slate-800" : ""}
          onClick={() => setHideIneligible((v) => !v)}
          data-testid="radar-hide-ineligible-toggle"
        >
          <Building2 className="w-3.5 h-3.5 mr-1.5" />
          {hideIneligible ? "Ocultando no aplicables" : "Mostrar no aplicables"}
        </Button>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-12 text-slate-500"><Loader2 className="w-6 h-6 animate-spin inline mr-2" />Cargando radar…</div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12 text-slate-500">
            <Radar className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No hay convocatorias.</p>
            {isAdmin && <p className="text-xs mt-2">Pulsa &quot;Actualizar ahora&quot; para escanear las fuentes, o añade una manual.</p>}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((c) => (
            <Card key={`${c.origin}-${c.id}`} className="hover:shadow-md transition-shadow" data-testid={`radar-call-${c.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <Badge className={`${SOURCE_COLORS[c.source]?.bg} ${SOURCE_COLORS[c.source]?.text} hover:opacity-90`}>
                        {SOURCE_COLORS[c.source]?.label || c.source}
                      </Badge>
                      {c.program && <span className="text-xs font-semibold text-slate-700">{c.program}</span>}
                      <DaysBadge days={c.days_until_deadline} />
                      <EligibilityBadge eligibility={c.eligibility} />
                      {c.assignments?.length > 0 && (
                        <Badge className="bg-teal-100 text-teal-800 hover:bg-teal-100 gap-1" data-testid={`solicitada-badge-${c.id}`}>
                          <Target className="w-3 h-3" /> Solicitada
                        </Badge>
                      )}
                    </div>
                    {c.url ? (
                      <a href={c.url} target="_blank" rel="noreferrer" className="text-base font-semibold text-slate-900 hover:text-emerald-700 inline-flex items-center gap-1">
                        {c.title}<ExternalLink className="w-3 h-3 opacity-60" />
                      </a>
                    ) : (
                      <span className="text-base font-semibold text-slate-900">{c.title}</span>
                    )}
                    {c.description && <p className="text-sm text-slate-600 mt-1 line-clamp-2">{c.description}</p>}
                    <div className="flex items-center gap-3 mt-2 text-xs text-slate-500 flex-wrap">
                      <span>Cierre: <strong className="text-slate-700">{c.deadline_text || (c.deadline ? new Date(c.deadline).toLocaleDateString("es-ES") : "TBD")}</strong></span>
                      {c.budget && <span>Presupuesto: <strong className="text-slate-700">{c.budget}</strong></span>}
                      {c.keywords_matched?.length > 0 && (
                        <span className="flex items-center gap-1">
                          {c.keywords_matched.slice(0, 3).map((kw) => (
                            <span key={kw} className="bg-slate-100 px-2 py-0.5 rounded-full text-slate-700">{kw}</span>
                          ))}
                        </span>
                      )}
                    </div>
                    {c.assignments?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`assignments-list-${c.id}`}>
                        {c.assignments.map((a, idx) => (
                          <span key={idx} className="inline-flex items-center gap-1 text-[11px] bg-teal-50 text-teal-800 border border-teal-200 rounded-full px-2 py-0.5">
                            {a.project_name} · {a.year}{a.expected_amount ? ` · ${Number(a.expected_amount).toLocaleString("es-ES")}€` : ""}
                            <button onClick={() => removeAssignment(c, idx)} data-testid={`remove-assignment-${c.id}-${idx}`}>
                              <X className="w-3 h-3 hover:text-red-600" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => openAssign(c)} data-testid={`radar-assign-${c.id}`}>
                      <Target className="w-3 h-3 mr-1" />Asignar a proyecto
                    </Button>
                    {c.origin === "radar" ? (
                      <>
                        <Button size="sm" variant="outline" onClick={() => handleSaveAsGrant(c.id)} disabled={saving[c.id] || c.saved_as_grant_id} data-testid={`radar-save-${c.id}`}>
                          {saving[c.id] ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                          {c.saved_as_grant_id ? "Guardada" : "Guardar"}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDismiss(c.id)} className="text-slate-500" data-testid={`radar-dismiss-${c.id}`}>
                          <EyeOff className="w-3 h-3 mr-1" /> Ocultar
                        </Button>
                      </>
                    ) : isAdmin && (
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteManual(c.id, c.title)} className="text-destructive" data-testid={`manual-delete-${c.id}`}>
                        <Trash2 className="w-3 h-3 mr-1" /> Eliminar
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!assignItem} onOpenChange={(o) => !o && setAssignItem(null)}>
        <DialogContent data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle>Marcar como Solicitada</DialogTitle>
            <DialogDescription>{assignItem?.title} — asígnala a un proyecto y año. Puedes añadir varias asignaciones.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Proyecto</Label>
              <Select value={assignProjectId} onValueChange={setAssignProjectId}>
                <SelectTrigger data-testid="assign-project-select"><SelectValue placeholder="Selecciona proyecto" /></SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Año</Label>
                <Input value={assignYear} onChange={(e) => setAssignYear(e.target.value)} placeholder="2026" data-testid="assign-year-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Importe esperado/concedido (€)</Label>
                <Input type="number" value={assignAmount} onChange={(e) => setAssignAmount(e.target.value)} placeholder="50000" data-testid="assign-amount-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignItem(null)}>Cancelar</Button>
            <Button onClick={submitAssign} disabled={assignSaving} data-testid="assign-save-btn">
              {assignSaving && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Eliminar convocatoria"
        description={`¿Estás seguro de que quieres eliminar "${deleteTarget?.name}"? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        onConfirm={confirmDeleteManual}
        loading={deleting}
        testId="delete-grant-confirm-dialog"
      />
    </div>
  );
}

function StatCard({ label, value, color }) {
  const display = (value === null || value === undefined || Number.isNaN(Number(value))) ? "—" : value;
  return (
    <Card>
      <CardContent className="p-3">
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">{label}</div>
        <div className={`text-2xl font-bold ${color}`}>{display}</div>
      </CardContent>
    </Card>
  );
}
